import { useEffect, useMemo, useState } from "react";
import { PixelRatio, Pressable, StyleSheet, View, type ViewProps } from "react-native";
import type { ColorValue } from "react-native/Libraries/StyleSheet/StyleSheet";
import OutsidePressHandler from "react-native-outside-press";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Button } from "~/components/Button";
import { Title } from "~/components/layout/text";
import { ErrorMessage } from "~/components/layout/text/ErrorMessage";
import { QendoIcon, type QendoIconSet } from "~/components/QendoIcon";

import { Space } from "~/constants/sizing";
import type { PartialPasscodeString, PasscodeString } from "~/providers/SessionProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { itemWidth, setDimensionsOnLayout } from "~/util/layout";
import { plural } from "~/util/string";

export type PasscodeProps = {
    onSubmit: (code: PasscodeString) => void;
    title?: string;
    error?: string | null;
    attempts?: number | null;
    onChange?: (code: PartialPasscodeString) => void;
    disabled?: boolean;
};

export type PasscodeInputProps = PasscodeProps & Omit<ViewProps, "children">;

const ICON_SIZE = 24;
type IconItem = {
    key: string;
    size: typeof ICON_SIZE;
    name: QendoIconSet;
    color: ColorValue;
};

export const PasscodeInput = ({
    title = "Enter your passcode",
    onSubmit,
    onChange,
    error,
    disabled,
    attempts = null,
    ...viewProps
}: PasscodeInputProps) => {
    const { colours, background } = useTheme();
    const [code, setCode] = useState<PartialPasscodeString>("");
    const [dimensions, setDimensions] = useState({ width: 300, height: 100 });
    const [focused, setFocused] = useState(true);
    const onLayout = setDimensionsOnLayout(setDimensions);
    const buttonWidth = useMemo(
        () => PixelRatio.roundToNearestPixel(itemWidth(dimensions.width, 3, Space.md)),
        [dimensions.width],
    );
    const { bottom } = useSafeAreaInsets();

    const length = code.length;
    const is_disabled = disabled === true || attempts === 0;
    const has_error = Boolean(error) || attempts === 0;

    const marginBottom = useMemo(
        () => (focused && !is_disabled ? dimensions.height : 0),
        [focused, is_disabled, dimensions.height],
    );

    const icons: IconItem[] = [...Array.from({ length: 4 }).keys()].map((index) => {
        const key = index + 1;
        const set = length >= key && !is_disabled && !has_error;

        return {
            key: `icon-${key}`,
            size: ICON_SIZE,
            name: set ? "circle-lg-filled" : "circle-lg-outline",
            color: set ? colours.icon.selected : has_error ? colours.icon.danger : colours.icon.secondary,
        } as IconItem;
    });

    const handleUpdate = (value: string) => {
        const characters = value
            .split("")
            .map((v) => parseInt(v, 10))
            .filter((v) => !Number.isNaN(v));

        const code = (characters.length > 4 ? characters.slice(4, 8) : characters).join("") as PartialPasscodeString;

        setCode(code);

        if (code.length === 4) {
            onSubmit(code as PasscodeString);
            setCode("");
        } else {
            onChange?.(code);
        }
    };

    const addNumber = (value: number) => {
        handleUpdate(`${code}${value}`);
    };

    const removeNumber = () => {
        handleUpdate(code.slice(0, -1));
    };

    const clear = () => {
        handleUpdate("");
    };

    const handleClick = () => {
        if (has_error) {
            clear();
        }

        setFocused(true);
    };

    const is_empty = code.length === 0;

    useEffect(() => {
        error && setCode("");
    }, [error]);

    const error_message =
        (attempts ? `Wrong passcode. You can try ${attempts} more ${plural("time", attempts)}.` : error) ?? "";

    return (
        <>
            <View {...viewProps} style={[styles.container, { marginBottom }, viewProps.style]}>
                <Title>{title}</Title>
                <ErrorMessage size="base" colour={error ? "danger" : "tertiary"} align="center">
                    {error_message}
                </ErrorMessage>
                <Pressable style={styles.indicators} onPress={handleClick} disabled={is_disabled}>
                    {icons.map(({ key, ...iconProps }) => (
                        <QendoIcon key={key} {...iconProps} />
                    ))}
                </Pressable>
            </View>
            {focused && !is_disabled && (
                <OutsidePressHandler
                    onOutsidePress={() => setFocused(false)}
                    style={[styles.board, background.primary, { bottom }]}
                >
                    <View style={styles.boardContainer} onLayout={onLayout}>
                        <Button style={{ width: buttonWidth }} size="lg" title="1" onPress={() => addNumber(1)} />
                        <Button style={{ width: buttonWidth }} size="lg" title="2" onPress={() => addNumber(2)} />
                        <Button style={{ width: buttonWidth }} size="lg" title="3" onPress={() => addNumber(3)} />
                        <Button style={{ width: buttonWidth }} size="lg" title="4" onPress={() => addNumber(4)} />
                        <Button style={{ width: buttonWidth }} size="lg" title="5" onPress={() => addNumber(5)} />
                        <Button style={{ width: buttonWidth }} size="lg" title="6" onPress={() => addNumber(6)} />
                        <Button style={{ width: buttonWidth }} size="lg" title="7" onPress={() => addNumber(7)} />
                        <Button style={{ width: buttonWidth }} size="lg" title="8" onPress={() => addNumber(8)} />
                        <Button style={{ width: buttonWidth }} size="lg" title="9" onPress={() => addNumber(9)} />
                        <Button
                            style={{ width: buttonWidth }}
                            size="lg"
                            variant="neutral"
                            icon="chevron-left"
                            onPress={removeNumber}
                            disabled={is_empty}
                        />
                        <Button style={{ width: buttonWidth }} size="lg" title="0" onPress={() => addNumber(0)} />
                        <Button
                            style={{ width: buttonWidth }}
                            size="lg"
                            variant="neutral"
                            icon="denied"
                            onPress={() => {
                                clear();
                                setFocused(false);
                            }}
                        />
                    </View>
                </OutsidePressHandler>
            )}
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        justifyContent: "flex-start",
        alignItems: "center",
        gap: Space.md,
    },
    input: {
        height: 1,
        overflow: "hidden",
    },
    indicators: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: Space.sm,
    },
    board: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: Space.md,
        zIndex: 5000,
    },
    boardContainer: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "stretch",
        flex: 3,
        gap: Space.sm,
    },
});
